import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Extension as ExtensionIcon,
  Code as CodeIcon,
  ExitToApp as LogoutIcon  
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, isMobile }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: isMobile ? 0 : `-${drawerWidth}px`,
    ...(open && !isMobile && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const menuItems = [
  { text: 'Panel de control', icon: <DashboardIcon />, path: '/', users: ['Administrador','Sistemas','Soporte','Consultas'] },
  { text: 'Usuarios', icon: <PeopleIcon />, path: '/users', users: ['Administrador','Sistemas'] },
  { text: 'Clientes', icon: <BusinessIcon />, path: '/clients', users: ['Administrador','Sistemas'] },
  { text: 'Productos', icon: <InventoryIcon />, path: '/products', users: ['Administrador','Sistemas'] },
  { text: 'Módulos', icon: <ExtensionIcon />, path: '/modules', users: ['Administrador','Sistemas'] },
  { text: 'Implementaciones', icon: <CodeIcon />, path: '/implementations', users: ['Administrador','Sistemas','Soporte','Consultas'] },
];

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const { user: currentUser } = useAuth();
  
  // Verificar autenticación periódicamente
  useEffect(() => {
    // Verificar autenticación al cargar el componente
    const initialCheck = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate('/login');
      }
    };
    
    initialCheck();
    
    // Verificar autenticación cada 5 minutos
    const interval = setInterval(async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate('/login');
      }
    }, 5 * 60 * 1000); // 5 minutos
    
    return () => clearInterval(interval);
  }, [checkAuth, navigate]);

  const canViewMenu = (u) => {
    return u.includes(currentUser.role);
  };

  const visibleMenuItems = menuItems.filter(item => canViewMenu(item.users));

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleNavigation = async (path) => {
    // Verificar autenticación antes de navegar
    const isAuthenticated = await checkAuth();
    if (isAuthenticated) {
      navigate(path);
    } else {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: isMobile ? '100%' : `calc(100% - ${open ? drawerWidth : 0}px)`,
          ml: isMobile ? 0 : `${open ? drawerWidth : 0}px`,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          Portal - Admin
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mr: 2,
              display: { xs: 'none', sm: 'block' } 
            }}
          >
            {user?.email}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >                        
        <Toolbar sx={{ justifyContent: 'center' }}>
          <img src="https://hcweb.dyntech.com.ar/Recursos/img/logo.png" style={{height:60}} alt=""/>
        </Toolbar>     
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {visibleMenuItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
            <Divider />
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Main open={open} isMobile={isMobile}>
        <Toolbar />
        <Outlet />
      </Main>
    </Box>
  );
};

export default Layout;